import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { del } from "@/lib/api";
import { centsEur } from "@/lib/documents";
import { date as deDate } from "@/lib/format";
import type { Approval, DocumentDetail } from "@/lib/types";
import { ApprovalFooter, ApprovalNoticeLine, askedAt } from "./ApprovalBanner";
import { useApprovalDecision, useRefreshDocument } from "./useDocument";

/**
 * „Zahlung zurücknehmen?“ for one payment of the list; with `approval` the agent's request (the payment from its
 * arguments) to approve or reject.
 */
export function ReversePaymentDialog({
  detail, paymentId, approval, onClose,
}: {
  detail: DocumentDetail;
  paymentId: number;
  approval?: Approval;
  onClose: () => void;
}) {
  const doc = detail.document;
  const refresh = useRefreshDocument(doc.id);
  const decision = useApprovalDecision({ id: approval?.id ?? 0, documentId: doc.id });
  const payment = detail.payments.find((p) => p.id === paymentId) ?? null;
  const asked = approval?.args.amountCents;
  const cents = payment?.amountCents ?? (typeof asked === "number" ? asked : null);
  const done = () => {
    onClose();
    toast.success(`Zahlung bei ${doc.number} zurückgenommen`);
  };
  const reverse = useMutation({
    mutationFn: () => del<{ status: "done" | "pending" }>(`/api/documents/${doc.id}/payments/${paymentId}`),
    onSuccess: (r) => {
      if (r.status === "pending") {
        onClose();
        toast.info("Wartet auf Freigabe");
        return;
      }
      done();
    },
    onSettled: refresh,
  });
  const error = reverse.error?.message ?? decision.approve.error?.message ?? null;
  const what = cents !== null ? `Die Zahlung über ${centsEur(cents)}${payment ? ` vom ${deDate(payment.paidOn)}` : ""}` : "Die Zahlung";

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="pr-6 text-left">
          {approval && <p className="text-[0.8125rem] text-muted-foreground">{approval.title} · {askedAt(approval.requestedAt)}</p>}
          <DialogTitle>Zahlung zurücknehmen?</DialogTitle>
          <DialogDescription className="text-pretty">
            {what} wird bei {doc.number} gelöscht; offener Betrag und Status werden neu berechnet.
          </DialogDescription>
        </DialogHeader>
        {approval && <ApprovalNoticeLine approval={approval} number={doc.number} />}
        {approval && !payment && approval.status === "pending" && (
          <p role="status" className="text-[0.8125rem] text-muted-foreground">Diese Zahlung gibt es nicht mehr.</p>
        )}
        {error && <p role="alert" className="text-[0.8125rem] text-destructive">{error}</p>}
        {approval ? (
          <ApprovalFooter
            approval={approval}
            primary="Freigeben & zurücknehmen"
            disabled={!payment || decision.approve.isPending}
            onApprove={() => decision.approve.mutate({}, { onSuccess: done })}
            onClose={onClose}
          />
        ) : (
          <DialogFooter>
            <Button variant="ghost" className="pointer-coarse:h-11" onClick={onClose}>Abbrechen</Button>
            <Button variant="destructive" className="pointer-coarse:h-11" disabled={reverse.isPending} onClick={() => reverse.mutate()}>
              Zurücknehmen
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
